import { useMemo, useState } from "react";
import type { Station, Trophic } from "../lib/types";
import { TROPHIC, TROPHIC_ORDER } from "../lib/trophic";
import { formatValue } from "@core/lib/format";

interface Props {
  stations: Station[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** Name of the active lake filter, for the empty-state wording. */
  lake: string | null;
}

export default function StationList({ stations, selectedId, onSelect, lake }: Props) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Trophic | "all">("all");

  const counts = useMemo(() => {
    const c = { all: stations.length } as Record<string, number>;
    for (const t of TROPHIC_ORDER) c[t] = stations.filter((s) => s.trophic === t).length;
    return c;
  }, [stations]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return stations
      .filter((s) => filter === "all" || s.trophic === filter)
      .filter((s) =>
        !needle ||
        s.siteId.toLowerCase().includes(needle) ||
        s.name.toLowerCase().includes(needle) ||
        s.lake.toLowerCase().includes(needle))
      .sort((a, b) =>
        a.lake.localeCompare(b.lake) || a.name.localeCompare(b.name));
  }, [stations, q, filter]);

  // Only offer the classes actually present, so the filter row does not fill
  // with chips that can never match.
  const chips = TROPHIC_ORDER.filter((t) => counts[t] > 0);

  return (
    <div className="list">
      <div className="list-controls">
        <input
          className="search"
          type="search"
          placeholder="Search station, name or lake"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search monitoring stations"
        />
        {chips.length > 1 && (
          <div className="chips" role="group" aria-label="Filter by trophic state">
            <button
              className={"chip" + (filter === "all" ? " on" : "")}
              onClick={() => setFilter("all")}
              aria-pressed={filter === "all"}
            >
              All <b>{counts.all}</b>
            </button>
            {chips.map((t) => (
              <button
                key={t}
                className={"chip" + (filter === t ? " on" : "")}
                onClick={() => setFilter(filter === t ? "all" : t)}
                aria-pressed={filter === t}
                style={{ ["--chip" as string]: TROPHIC[t].color }}
              >
                <i className="dot" /> {TROPHIC[t].short} <b>{counts[t]}</b>
              </button>
            ))}
          </div>
        )}
      </div>

      <ul className="rows">
        {shown.map((s) => {
          const on = s.key === selectedId;
          const t = TROPHIC[s.trophic];
          return (
            <li key={s.key}>
              <button
                className={"row" + (on ? " on" : "")}
                onClick={() => onSelect(on ? null : s.key)}
                aria-current={on ? "true" : undefined}
              >
                <span className="row-bar" style={{ background: t.color }} />
                <span className="row-main">
                  <span className="row-id">{s.siteId}</span>
                  <span className="row-name">{s.name}</span>
                  <span className="row-meta">
                    {s.lake} · {t.short}
                    {s.lastYear ? ` · to ${s.lastYear}` : ""}
                  </span>
                </span>
                <span className="row-reading">
                  <span className="row-reading-label">Secchi</span>
                  <span className="row-reading-value" style={{ color: t.color }}>
                    {formatValue(s.latest.secchiFt, 1)}
                  </span>
                  <span className="row-reading-unit">feet</span>
                </span>
              </button>
            </li>
          );
        })}
        {shown.length === 0 && (
          <li className="empty-row">
            No stations match that{lake ? ` on ${lake}` : ""}.
          </li>
        )}
      </ul>
    </div>
  );
}
